import React, { useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { mapleOSHeroSliderScreenshots } from "./mapleosHeroSliderConfig";

export default function DesktopHeroDisplay() {
  const shouldReduceMotion = useReducedMotion();
  const [carouselApi, setCarouselApi] = useState<CarouselApi>();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!carouselApi) {
      return;
    }

    const syncIndex = () => {
      setCurrentIndex(carouselApi.selectedScrollSnap());
    };

    syncIndex();
    carouselApi.on("select", syncIndex);
    carouselApi.on("reInit", syncIndex);

    return () => {
      carouselApi.off("select", syncIndex);
      carouselApi.off("reInit", syncIndex);
    };
  }, [carouselApi]);

  const currentScreenshot = mapleOSHeroSliderScreenshots[currentIndex] ?? mapleOSHeroSliderScreenshots[0];

  return (
    <div className="relative mx-auto w-full max-w-[86rem] px-2 sm:px-4">
      <motion.div
        initial={{ opacity: 0, y: 28, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.75, delay: 0.08 }}
        className="relative mx-auto w-full max-w-[76rem]"
      >
        <div className="relative mx-auto w-full max-w-[76rem]">
          <div className="relative border border-white/10 bg-[linear-gradient(180deg,#262626_0%,#111214_20%,#08090b_100%)] p-3 shadow-[0_28px_80px_rgba(0,0,0,0.38)] sm:p-4 md:p-5">
            <div className="absolute inset-x-0 top-0 h-3 bg-[linear-gradient(180deg,rgba(255,255,255,0.18),rgba(255,255,255,0))] opacity-60" />
            <div className="border border-white/8 bg-[#04070c] p-3 sm:p-4 md:p-5">
              <div className="relative overflow-hidden border border-white/10 bg-black shadow-[inset_0_0_0_1px_rgba(255,255,255,0.04)]">
                <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex h-10 items-center justify-between border-b border-white/10 bg-[linear-gradient(180deg,rgba(12,18,28,0.9),rgba(7,12,20,0.9))] px-4 backdrop-blur sm:px-5">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-primary/90 shadow-[0_0_10px_hsl(var(--primary)/0.55)]" />
                  </div>
                  <div className="h-1.5 w-20 rounded-full bg-white/10 sm:w-32" />
                  <div className="max-w-[38%] truncate text-right text-sm font-semibold tracking-[-0.03em] text-white/78 sm:text-base">
                    {currentScreenshot.title}
                  </div>
                </div>

                <div className="border-b border-white/10 bg-[#05070c] pt-10">
                  <div className="relative aspect-[16/9] w-full bg-[#05070c]">
                    <Carousel setApi={setCarouselApi} opts={{ align: "start", loop: true }} className="h-full w-full">
                      <CarouselContent className="-ml-0 h-full">
                        {mapleOSHeroSliderScreenshots.map((screenshot, index) => (
                          <CarouselItem key={screenshot.image} className="basis-full pl-0">
                            <motion.div
                              whileHover={shouldReduceMotion ? undefined : { scale: 1.003 }}
                              className="relative h-full w-full bg-[#05070c]"
                            >
                              <img
                                src={screenshot.image}
                                alt={screenshot.title}
                                className="h-full w-full object-contain"
                                loading={index === 0 ? "eager" : "lazy"}
                              />
                            </motion.div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious className="left-3 z-30 h-12 w-12 rounded-full border-white/25 bg-black/72 text-white shadow-[0_10px_28px_rgba(0,0,0,0.4)] backdrop-blur-md hover:bg-black/85 hover:text-white [&_svg]:h-6 [&_svg]:w-6 md:left-5 md:h-14 md:w-14 md:[&_svg]:h-7 md:[&_svg]:w-7" />
                      <CarouselNext className="right-3 z-30 h-12 w-12 rounded-full border-white/25 bg-black/72 text-white shadow-[0_10px_28px_rgba(0,0,0,0.4)] backdrop-blur-md hover:bg-black/85 hover:text-white [&_svg]:h-6 [&_svg]:w-6 md:right-5 md:h-14 md:w-14 md:[&_svg]:h-7 md:[&_svg]:w-7" />
                    </Carousel>
                  </div>
                </div>

                <div className="flex min-h-[128px] items-start justify-between gap-6 bg-[linear-gradient(180deg,#090d14_0%,#06090f_100%)] px-6 py-5 text-left sm:px-8">
                  <div className="min-w-0 max-w-3xl text-left">
                    <div className="text-[11px] uppercase tracking-[0.28em] text-white/52">{currentScreenshot.eyebrow}</div>
                    <div className="mt-3 text-sm leading-6 text-white/60 sm:text-base">
                      {currentScreenshot.description}
                    </div>
                  </div>
                  <div className="shrink-0 pt-0.5 text-base tabular-nums text-white/40 sm:text-lg">
                    {String(currentIndex + 1).padStart(2, "0")} / {String(mapleOSHeroSliderScreenshots.length).padStart(2, "0")}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="pointer-events-none relative mx-auto h-14 w-20 bg-[linear-gradient(180deg,#4a4a4d_0%,#1f2023_30%,#0f1012_100%)] shadow-[0_20px_30px_rgba(0,0,0,0.28)]" />
          <div className="pointer-events-none relative mx-auto h-6 w-[42%] min-w-[16rem] max-w-[34rem] bg-[linear-gradient(180deg,#6a6b6f_0%,#2f3034_24%,#111214_100%)] shadow-[0_30px_60px_rgba(0,0,0,0.25)] before:absolute before:inset-x-[10%] before:top-[2px] before:h-[2px] before:bg-white/25 before:content-['']" />
        </div>
      </motion.div>
    </div>
  );
}